import {wupoServerUri} from '../Api';

// Reads the current tenant self-serve onboarding status for the authenticated
// user. Mirrors the AdminUtil convention: builds the base URL from
// `wupoServerUri.devnet`, sends the JWT as a Bearer token, and returns a
// normalized { ok, status, data, error } envelope so callers can branch
// without re-parsing the response.
const getOnboardingStatus = async (jwtToken) => {
  const response = await fetch(`${wupoServerUri.devnet}/api/secure/onboarding/status`, {
    method: "GET",
    credentials: 'include',
    headers: {
      "Authorization": `Bearer ${jwtToken}`,
      "Content-Type": "application/json; charset=UTF-8",
    },
  }).catch((error) => {
    console.log("Error: " + error);
  });

  if (response && response.status === 200) {
    return { ok: true, data: await response.json() };
  }
  if (response) {
    let errBody = null;
    try { errBody = await response.json(); } catch (_) { /* ignore */ }
    return { ok: false, status: response.status, error: errBody && errBody.error ? errBody.error : `Request failed (${response.status})` };
  }
  return { ok: false, error: "Network error" };
};

// Lightweight poller. Calls getOnboardingStatus every `intervalMs`, forwards
// each successful payload to `onUpdate(data)`, and stops once `isDone(data)`
// returns true (or on repeated failures if a caller wants to bail). Returns a
// cancel function so a component can clear the loop on unmount.
const pollOnboardingStatus = (jwtToken, onUpdate, { intervalMs = 2500, isDone } = {}) => {
  let cancelled = false;
  let timer = null;

  const tick = async () => {
    if (cancelled) return;
    const res = await getOnboardingStatus(jwtToken);
    if (cancelled) return;
    if (res.ok) {
      if (typeof onUpdate === 'function') onUpdate(res.data);
      if (typeof isDone === 'function' && isDone(res.data)) return;
    }
    timer = setTimeout(tick, intervalMs);
  };

  tick();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
};

// --- PUBLIC (no-login) self-serve onboarding calls -------------------------
// These hit the unauthenticated `/api/auth/onboarding/**` surface: no Bearer
// token is attached. They keep the same normalized { ok, status, data, error }
// envelope as getOnboardingStatus so the public wizard pages can branch the
// same way. A generic error message is surfaced on failure (the server never
// leaks whether a token/email exists).
const PUBLIC_HEADERS = { "Content-Type": "application/json; charset=UTF-8" };

// Shared response normalizer for the public POST/GET calls above.
const normalize = async (response) => {
  if (response && (response.status === 200 || response.status === 201)) {
    let data = null;
    try { data = await response.json(); } catch (_) { /* empty body */ }
    return { ok: true, status: response.status, data };
  }
  if (response) {
    let errBody = null;
    try { errBody = await response.json(); } catch (_) { /* ignore */ }
    return {
      ok: false,
      status: response.status,
      error: errBody && errBody.error ? errBody.error : `Request failed (${response.status})`,
    };
  }
  return { ok: false, error: "Network error" };
};

// Step 1 — submit the org name + admin email against the onboarding token that
// the payment redirect carried. → { paymentRef, status }.
const submitOnboarding = async (onboardingToken, { orgName, email, adminName } = {}) => {
  const response = await fetch(`${wupoServerUri.devnet}/api/auth/onboarding/submit`, {
    method: "POST",
    headers: PUBLIC_HEADERS,
    body: JSON.stringify({ onboardingToken, orgName, email, adminName }),
  }).catch((error) => { console.log("Error: " + error); });
  return normalize(response);
};

// Step 2 — poll provisioning progress by onboarding token. → { status, error }.
const getPublicOnboardingStatus = async (onboardingToken) => {
  const response = await fetch(
    `${wupoServerUri.devnet}/api/auth/onboarding/status?token=${encodeURIComponent(onboardingToken)}`,
    { method: "GET", headers: PUBLIC_HEADERS },
  ).catch((error) => { console.log("Error: " + error); });
  return normalize(response);
};

// Activation — validate the emailed activation token. → { valid, email, orgName }.
const validateActivationToken = async (token) => {
  const response = await fetch(`${wupoServerUri.devnet}/api/auth/onboarding/activate/validate`, {
    method: "POST",
    headers: PUBLIC_HEADERS,
    body: JSON.stringify({ token }),
  }).catch((error) => { console.log("Error: " + error); });
  return normalize(response);
};

// Activation — set the password and receive a JwtResponse (auto-login payload).
const activateAccount = async (token, password) => {
  const response = await fetch(`${wupoServerUri.devnet}/api/auth/onboarding/activate`, {
    method: "POST",
    headers: PUBLIC_HEADERS,
    body: JSON.stringify({ token, password }),
  }).catch((error) => { console.log("Error: " + error); });
  return normalize(response);
};

// Resend the activation email. Server always returns a generic confirmation.
const resendActivation = async (email) => {
  const response = await fetch(`${wupoServerUri.devnet}/api/auth/onboarding/activate/resend`, {
    method: "POST",
    headers: PUBLIC_HEADERS,
    body: JSON.stringify({ email }),
  }).catch((error) => { console.log("Error: " + error); });
  return normalize(response);
};

export {
  getOnboardingStatus,
  pollOnboardingStatus,
  submitOnboarding,
  getPublicOnboardingStatus,
  validateActivationToken,
  activateAccount,
  resendActivation,
};
